import StoryblokClient from 'storyblok-js-client'
import { useDiscoverStore } from '@/stores/discover'

// Cache for the Storyblok instance
let storyblokInstance: StoryblokClient | null = null;

/**
 * Create a StoryblokClient instance.
 * @param token Storyblok API token
 */
async function getStoryblokInstance(): Promise<StoryblokClient> {
  if (!storyblokInstance) {
    // Fetch config from store
    const discoverStore = useDiscoverStore()
    const apiKey = discoverStore.configuration?.uris?.API_CMS_KEY

    if (!apiKey) {
      // Throw an error or handle missing API key appropriately
      // Ensure the discoverStore is populated before calling this function
      throw new Error('Storyblok API Key not found in configuration. Ensure discoverStore is initialized.')
    }

    const Storyblok = new StoryblokClient({
      accessToken: apiKey,
      cache: {
        clear: 'auto',
        type: 'memory',
      }
    })

    Storyblok.setComponentResolver((component, blok) => {
      return `<component blok='${JSON.stringify(blok)}' is="${component}"></component>`
    })

    storyblokInstance = Storyblok
  }

  return storyblokInstance;
}

// Cache for the current version and the pending promise
let _cachedVersion: number | null = null;
let _versionPromise: Promise<number | null> | null = null;

/**
 * Fetches the version from the Storyblok API
 * @returns A promise that resolves to the version number or null if there was an error
 */
async function fetchVersion(): Promise<number | null> {
  try {
    console.log('Fetching space data to get version...');
    const instance = await getStoryblokInstance();
    const spaceResponse = await instance.get('cdn/spaces/me');

    // Get the version from the response
    const version = Number(spaceResponse?.data?.space?.version);
    _cachedVersion = version;

    console.log(`Space version fetched: ${version}`);
    return version;
  } catch (error) {
    console.error('Error getting space version:', error);
    return null;
  } finally {
    // Clear the promise reference once it's done
    _versionPromise = null;
  }
}

/**
 * Gets the current version from space
 * @returns The current version to use for API calls
 */
export async function getCurrentVersion(): Promise<number> {
  // If we already have a cached version, return it immediately
  if (_cachedVersion) {
    return _cachedVersion;
  }

  // If we're already fetching the version, wait for that promise to resolve
  if (_versionPromise) {
    try {
      const version = await _versionPromise;
      return version ?? 0;
    } catch (error) {
      console.error('Error waiting for version promise:', error);
      return 0;
    }
  }

  // Otherwise, create a new promise to fetch the version
  _versionPromise = fetchVersion();

  // Wait for the promise to complete
  const version = await _versionPromise;
  return version ?? 0;
}

/**
 * Gets a configured Storyblok instance with the current version
 * @returns A Promise resolving to {storyblokInstance, currentVersion}
 */
export async function getConfiguredInstance() {
  const currentVersion = await getCurrentVersion();
  const storyblokInstance = await getStoryblokInstance();
  return { storyblokInstance, currentVersion };
}
